// @ts-nocheck
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "context/ToastContext";
import InputField from "components/form/InputField";
import ToggleInput from "components/form/toggle/ToggleInput";
import Button from "components/ui/buttons/Button";
import SearchableDropdown from "components/form/search/SearchableDropdown";
import useCreateLocation from "hooks/locations/useCreateLocation";
import MediaUploadField from "components/form/filesUpload/MediaUploadField";
import type { PresignedUploadResult } from "hooks/storage/usePresignedUpload";
import { COUNTRIES } from "constants/lists";
import PageHeader from "components/ui/PageHeader";

const COUNTRY_OPTIONS = COUNTRIES.map((c) => ({ value: c.name, label: c.name }));

const LocationCreatePage = () => {
  const navigate = useNavigate();
  const { addToast } = useToast();
  const { createLocation, loading, error, fieldErrors } = useCreateLocation();

  const [form, setForm] = useState({
    name:            "",
    city:            "",
    country:         "",
    address:         "",
    venue_details:   "",
    latitude:        "",
    longitude:       "",
    is_active:       true,
    contact_phone:   "",
    whatsapp_number: "",
  });
  const [thumbnailKey, setThumbnailKey] = useState("");
  const [thumbnailUrl, setThumbnailUrl] = useState("");

  const handleThumbnailChange = (result: PresignedUploadResult | null) => {
    setThumbnailKey(result ? result.file_key  : "");
    setThumbnailUrl(result ? result.public_url : "");
  };

  const updateFormData = (key: string, value: any) =>
    setForm((p) => ({ ...p, [key]: value }));

  const isFormValid =
    form.name.trim() !== "" &&
    form.city.trim() !== "" &&
    form.country !== "" &&
    form.address.trim() !== "" &&
    form.contact_phone.trim() !== "" &&
    form.whatsapp_number.trim() !== "";

  const handleSubmit = async (e) => {
    e.preventDefault();
    const { location: created, fieldErrors: fe, error: ge } = await createLocation({
      ...form,
      ...(thumbnailKey && { thumbnail: thumbnailKey }),
    });
    if (created) {
      addToast("Location created successfully", "success");
      navigate("/account-manager/locations");
    } else {
      const firstFieldError = Object.values(fe)[0];
      addToast(firstFieldError ?? ge ?? "Failed to create location. Please try again.", "error");
    }
  };

  return (
    <div className="max-w-5xl mx-auto">
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm">
        <PageHeader
          title="Create Location"
          subtitle="Fill in the details to add a new location"
          bordered
        />

        <form onSubmit={handleSubmit} className="px-6 py-5 grid grid-cols-1 gap-4">
          {error && (
            <div className="rounded-xl bg-red-50 border border-red-200 px-4 py-2.5 text-sm text-red-600">
              {error}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <InputField
              label="Location Name"
              field="name"
              placeholder="Main Campus"
              formData={form}
              errors={fieldErrors}
              updateFormData={updateFormData}
            />
            <InputField
              label="City"
              field="city"
              placeholder="Dubai"
              formData={form}
              errors={fieldErrors}
              updateFormData={updateFormData}
            />
            <SearchableDropdown
              label="Country"
              field="country"
              options={COUNTRY_OPTIONS}
              formData={form}
              errors={fieldErrors}
              updateFormData={updateFormData}
              placeholder="Select country..."
            />
            <InputField
              label="Address"
              field="address"
              placeholder="123 Main St"
              formData={form}
              errors={fieldErrors}
              updateFormData={updateFormData}
            />
            <InputField
              label="Latitude"
              field="latitude"
              placeholder="25.2048"
              required={false}
              formData={form}
              errors={fieldErrors}
              updateFormData={updateFormData}
            />
            <InputField
              label="Longitude"
              field="longitude"
              placeholder="55.2708"
              required={false}
              formData={form}
              errors={fieldErrors}
              updateFormData={updateFormData}
            />
            <InputField
              label="Contact Phone"
              field="contact_phone"
              placeholder="+971 50 000 0000"
              formData={form}
              errors={fieldErrors}
              updateFormData={updateFormData}
            />
            <InputField
              label="WhatsApp Number"
              field="whatsapp_number"
              placeholder="+971 50 000 0000"
              formData={form}
              errors={fieldErrors}
              updateFormData={updateFormData}
            />
          </div>

          <InputField
            label="Venue Details"
            field="venue_details"
            placeholder="Floor 3, Building A..."
            required={false}
            formData={form}
            errors={fieldErrors}
            updateFormData={updateFormData}
          />

          <MediaUploadField
            label="Thumbnail"
            type="image"
            folder="locations/thumbnails"
            displayUrl={thumbnailUrl}
            onChange={handleThumbnailChange}
            error={fieldErrors?.thumbnail}
          />

          <ToggleInput
            label="Active"
            field="is_active"
            formData={form}
            errors={fieldErrors}
            updateFormData={updateFormData}
          />

          <div className="flex gap-2 border-t border-slate-100 pt-5">
            <Button
              type="button"
              text="Cancel"
              onClick={() => navigate("/account-manager/locations")}
              className="flex-1 py-2.5"
              bgColor="bg-white"
              textColor="text-slate-600"
              borderColor="border-slate-200"
              hoverBgColor="hover:bg-slate-50"
              hoverTextColor=""
              hoverBorderColor=""
            />
            <Button
              type="submit"
              variant="primary"
              text={loading ? "Creating..." : "Create Location"}
              disabled={loading || !isFormValid}
              className="flex-1 py-2.5"
            />
          </div>
        </form>
      </div>
    </div>
  );
};

export default LocationCreatePage;
